import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Badge } from "@shared/components/ui/badge";
import { Checkbox } from "@shared/components/ui/checkbox";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import {
  Loader2,
  Search,
  UserPlus,
  UserMinus,
  Store,
  MapPin,
} from "lucide-react";
import {
  useAssignedClientsQuery,
  useUnassignedClientsQuery,
  useAssignClientsMutation,
  useUnassignClientsMutation,
} from "@features/client-support/hooks/useClientSupport";
import { toast } from "sonner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";

export default function AssignClientsDialog({
  open,
  onOpenChange,
  supportUser,
}) {
  const { t } = useTranslation();
  const [searchAssigned, setSearchAssigned] = useState("");
  const [searchUnassigned, setSearchUnassigned] = useState("");
  const [selectedToAssign, setSelectedToAssign] = useState([]);
  const [selectedToUnassign, setSelectedToUnassign] = useState([]);

  // Fetch assigned clients
  const {
    data: assignedData,
    isLoading: loadingAssigned,
    refetch: refetchAssigned,
  } = useAssignedClientsQuery(supportUser?._id, {
    search: searchAssigned,
    limit: 100,
  });

  // Fetch unassigned clients
  const {
    data: unassignedData,
    isLoading: loadingUnassigned,
    refetch: refetchUnassigned,
  } = useUnassignedClientsQuery({
    search: searchUnassigned,
    limit: 100,
  });

  // Mutations
  const assignMutation = useAssignClientsMutation();
  const unassignMutation = useUnassignClientsMutation();

  const assignedClients = assignedData?.data || [];
  const unassignedClients = unassignedData?.data || [];

  // Reset selections when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedToAssign([]);
      setSelectedToUnassign([]);
      setSearchAssigned("");
      setSearchUnassigned("");
    }
  }, [open]);

  const handleAssign = async () => {
    if (selectedToAssign.length === 0) {
      toast.warning(t("Please select at least one client to assign"));
      return;
    }

    try {
      await assignMutation.mutateAsync({
        supportId: supportUser._id,
        clientIds: selectedToAssign,
      });
      toast.success(
        t("Successfully assigned {count} client(s)", {
          count: selectedToAssign.length,
        })
      );
      setSelectedToAssign([]);
      refetchAssigned();
      refetchUnassigned();
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("Failed to assign clients")
      );
    }
  };

  const handleUnassign = async () => {
    if (selectedToUnassign.length === 0) {
      toast.warning(t("Please select at least one client to unassign"));
      return;
    }

    try {
      await unassignMutation.mutateAsync({
        supportId: supportUser._id,
        clientIds: selectedToUnassign,
      });
      toast.success(
        t("Successfully unassigned {count} client(s)", {
          count: selectedToUnassign.length,
        })
      );
      setSelectedToUnassign([]);
      refetchAssigned();
      refetchUnassigned();
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("Failed to unassign clients")
      );
    }
  };

  const toggleClientToAssign = (clientId) => {
    setSelectedToAssign((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const toggleClientToUnassign = (clientId) => {
    setSelectedToUnassign((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const selectAllToAssign = () => {
    if (selectedToAssign.length === unassignedClients.length) {
      setSelectedToAssign([]);
    } else {
      setSelectedToAssign(unassignedClients.map((c) => c._id));
    }
  };

  const selectAllToUnassign = () => {
    if (selectedToUnassign.length === assignedClients.length) {
      setSelectedToUnassign([]);
    } else {
      setSelectedToUnassign(assignedClients.map((c) => c._id));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>{t("Assign Clients to Client Support")}</DialogTitle>
          <DialogDescription>
            {t("Manage client assignments for")}{" "}
            <strong>{supportUser?.fullName}</strong>
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="assign" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="assign">
              <UserPlus className="h-4 w-4 mr-2" />
              {t("Assign Clients")} ({unassignedClients.length})
            </TabsTrigger>
            <TabsTrigger value="assigned">
              <UserMinus className="h-4 w-4 mr-2" />
              {t("Assigned Clients")} ({assignedClients.length})
            </TabsTrigger>
          </TabsList>

          {/* Assign Tab */}
          <TabsContent value="assign" className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("Search unassigned clients...")}
                  value={searchUnassigned}
                  onChange={(e) => setSearchUnassigned(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllToAssign}
                disabled={unassignedClients.length === 0}
              >
                {selectedToAssign.length === unassignedClients.length
                  ? t("Deselect All")
                  : t("Select All")}
              </Button>
            </div>

            {selectedToAssign.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm">
                  {selectedToAssign.length} {t("client(s) selected")}
                </span>
                <Button
                  size="sm"
                  onClick={handleAssign}
                  disabled={assignMutation.isPending}
                >
                  {assignMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {t("Assign Selected")}
                </Button>
              </div>
            )}

            <ScrollArea className="h-[300px] border rounded-lg p-2">
              {loadingUnassigned ? (
                <div className="flex justify-center py-8">
                  <ThreeDotLoader />
                </div>
              ) : unassignedClients.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {t("No unassigned clients found")}
                </div>
              ) : (
                <div className="space-y-2">
                  {unassignedClients.map((client) => (
                    <div
                      key={client._id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer hover:bg-accent ${
                        selectedToAssign.includes(client._id)
                          ? "bg-accent border-primary"
                          : ""
                      }`}
                      onClick={() => toggleClientToAssign(client._id)}
                    >
                      <Checkbox
                        checked={selectedToAssign.includes(client._id)}
                        onCheckedChange={() => toggleClientToAssign(client._id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{client.fullName}</div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Store className="h-3 w-3" />
                            {client.brandName}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {client.city}
                          </span>
                        </div>
                      </div>
                      <Badge variant="outline">{client.phone}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Assigned Tab */}
          <TabsContent value="assigned" className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("Search assigned clients...")}
                  value={searchAssigned}
                  onChange={(e) => setSearchAssigned(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllToUnassign}
                disabled={assignedClients.length === 0}
              >
                {selectedToUnassign.length === assignedClients.length
                  ? t("Deselect All")
                  : t("Select All")}
              </Button>
            </div>

            {selectedToUnassign.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm">
                  {selectedToUnassign.length} {t("client(s) selected")}
                </span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleUnassign}
                  disabled={unassignMutation.isPending}
                >
                  {unassignMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {t("Unassign Selected")}
                </Button>
              </div>
            )}

            <ScrollArea className="h-[300px] border rounded-lg p-2">
              {loadingAssigned ? (
                <div className="flex justify-center py-8">
                  <ThreeDotLoader />
                </div>
              ) : assignedClients.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {t("No assigned clients found")}
                </div>
              ) : (
                <div className="space-y-2">
                  {assignedClients.map((client) => (
                    <div
                      key={client._id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer hover:bg-accent ${
                        selectedToUnassign.includes(client._id)
                          ? "bg-accent border-destructive"
                          : ""
                      }`}
                      onClick={() => toggleClientToUnassign(client._id)}
                    >
                      <Checkbox
                        checked={selectedToUnassign.includes(client._id)}
                        onCheckedChange={() =>
                          toggleClientToUnassign(client._id)
                        }
                      />
                      <div className="flex-1">
                        <div className="font-medium">{client.fullName}</div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Store className="h-3 w-3" />
                            {client.brandName}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {client.city}
                          </span>
                        </div>
                      </div>
                      <Badge variant="outline">{client.phone}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
